<script setup>
import Card from '@/components/Shared/Card.vue';
import {inject, ref, computed} from 'vue';
import {useForm} from '@inertiajs/vue3';
import FormEditor from '@/components/Shared/Form/Editor/Index.vue';
import FormInput from "@/components/Shared/Form/Input/Index.vue";
import {debounce} from "lodash";

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    default: 'customer',
  },
  availableReplyIds: {
    type: Array,
  }
});

const emit = defineEmits(['submit', 'reloadReplies']);

const trans = inject('trans');

const editor = ref(null);
const showPermissionError = ref(false);
const findReplyReferencesRegex = ref(new RegExp(/!(\d+)([.,\s])/g));

const replyForm = useForm({
  message: '',
  private: false,
});

const submit = () => {
  if (isAdmin.value) {
    showPermissionError.value = false;
    axios.get(route('partner-support.tickets.check-permissions', props.ticket.id))
      .then(() => {
        reply()
      })
      .catch(() => {
        showPermissionError.value = true;
      });
  } else {
    reply()
  }
};

const reply = () => {
  let replyRoute = 'dashboard.support.ticket.reply';
  if (isAdmin.value){
    replyRoute = 'partner-support.tickets.reply';
  }
  if (isPartner.value){
    replyRoute = 'dashboard.partner.support.ticket.reply';
  }

  replyForm.post(route(replyRoute, props.ticket.id), {
    preserveScroll: true,
    onSuccess: () => {
      replyForm.reset();
      editor.value.reset();
      emit('reloadReplies')
    },
  });
}

const checkForLinkableReplies = debounce(() => {
  replyForm.message = replyForm.message.replace(findReplyReferencesRegex.value, function (matched, group1, group2) {
    if (props.availableReplyIds.includes(parseInt(group1))) {
      return `<a href="#${group1}">!${group1}</a>${group2}`
    }
    return matched
  }, 500)
})

const isCustomer = computed(() => props.type === 'customer')
const isPartner = computed(() => props.type === 'partner')
const isAdmin = computed(() => props.type === 'admin')

</script>

<template>
  <card>
    <template #header>
      <div class="d-flex justify-content-sm-between gap">
        <div v-if="!replyForm.private" class="font-bold text-lg">
          {{ trans('__JSON__.Reply To') }}
          <span class="text-muted font-medium ml-2">{{ ticket.user.name }}</span>
        </div>
        <div v-else class="font-bold text-lg">
          {{ trans('__JSON__.Private note') }}
        </div>

        <form-input
          v-if="isAdmin"
          id="private"
          v-model="replyForm.private"
          :label-text="trans('__JSON__.Private')"
          :placeholder="trans('__JSON__.Private')"
          label="name"
          type="checkbox"
          containerClass="form_knob_min_width"
        />
      </div>
    </template>
    <template #body>
      <div>
        <form-editor
          ref="editor"
          v-model="replyForm.message"
          :error="replyForm.errors.message"
          style="background: white"
          @update:modelValue="checkForLinkableReplies"
        ></form-editor>
      </div>
      <div v-show="showPermissionError" class="alert alert-danger">Oops! Looks like something went wrong. Copy your
        message and refresh the page.
      </div>
    </template>
    <template #footer>
      <slot :submit="submit" :replyForm="replyForm" name="footer">
        <div class="d-flex justify-content-end">
          <button class="button" @click="submit">{{ trans('__JSON__.Reply') }}</button>
        </div>
      </slot>
    </template>
  </card>
</template>
